import { ReactElement, useState } from 'react';
import { Component as ChevronDownIcon } from 'icons/icon-ui--chevron-down.svg';
import { Component as ChevronUpIcon } from 'icons/icon-ui--chevron-up.svg';
import { Button } from '../shared/button/Button';
import { CheckboxList } from '../shared/checkbox/CheckboxList';
import { Dropdown } from '../shared/dropdown/Dropdown';
import { initialDropdownFilter, SelectedDropdownFilters } from '../../constants/menu';
import styles from './MenuPage.module.scss';

type Props = {
  selectedDropdownFilters: SelectedDropdownFilters,
  setSelectedDropdownFilters: (selectedDropdownFilters: SelectedDropdownFilters, userInitiated: boolean) => void,
  filteredResultCount: number,
  triggerTileShiftAnimation: () => void;
}

export function FilterDropdown({
  selectedDropdownFilters,
  setSelectedDropdownFilters,
  filteredResultCount,
  triggerTileShiftAnimation,
}: Props): ReactElement {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProductTypeOpen, setIsProductTypeOpen] = useState(true);
  const [isCookTimeOpen, setIsCookTimeOpen] = useState(true);

  const selectedFilterCount = selectedDropdownFilters.productType.length
    + selectedDropdownFilters.cookTime.length;

  const isChecked = (category: string, value: string) => (
    selectedDropdownFilters[category].includes(value)
  );

  const handleSelectFilter = (category: string, value: string) => {
    let selectedFilters: SelectedDropdownFilters;
    if (isChecked(category, value)) {
      selectedFilters = {
        ...selectedDropdownFilters,
        [category]: selectedDropdownFilters[category].filter(
          (filterValue: string) => filterValue !== value,
        ),
      };
    } else {
      selectedFilters = {
        ...selectedDropdownFilters,
        [category]: [...selectedDropdownFilters[category], value],
      };
    }
    setSelectedDropdownFilters(selectedFilters, true);
    triggerTileShiftAnimation();
  };

  const handleResetFilters = () => {
    setSelectedDropdownFilters(initialDropdownFilter, true);
    triggerTileShiftAnimation();
  };

  return (
    <Dropdown
      target={(
        <button
          type="button"
          className={`${styles.filter_dropdown_button} ${selectedFilterCount > 0 ? styles.with_selections : ''}`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          Filter
          {selectedFilterCount > 0 && (
            <div className={styles.margin_left}>
              •
              <span className={styles.margin_left}>{selectedFilterCount}</span>
            </div>
          )}
          {isDropdownOpen
            ? <ChevronUpIcon width="9px" style={{ marginLeft: '5px' }} />
            : <ChevronDownIcon width="9px" style={{ marginLeft: '5px' }} />}
        </button>
      )}
      footer={(
        <div className={styles.filter_dropdown_footer}>
          <Button text="Reset" onClick={handleResetFilters} isSecondary />
          <Button
            text={`View ${filteredResultCount} Results`}
            onClick={() => setIsDropdownOpen(false)}
          />
        </div>
      )}
      isOpen={isDropdownOpen}
      onClose={() => setIsDropdownOpen(false)}
      width="230px"
    >
      <div className={styles.filter_dropdown_container}>
        <div>
          <div
            role="button"
            onClick={() => setIsProductTypeOpen(!isProductTypeOpen)}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setIsProductTypeOpen(!isProductTypeOpen)}
            className={styles.filter_category_header}
          >
            Product Type
            {isProductTypeOpen ? <ChevronUpIcon width="9px" /> : <ChevronDownIcon width="9px" />}
          </div>
          <hr style={{ marginTop: '12px' }} />
          {isProductTypeOpen && (
            <div className={styles.checkboxes_container}>
              <CheckboxList
                color="#297B03"
                checkboxes={[
                  {
                    label: 'Meal Kit',
                    onChange: () => handleSelectFilter('productType', 'Meal Kit'),
                    isChecked: isChecked('productType', 'Meal Kit'),
                  },
                  {
                    label: 'Ready-to-Eat',
                    onChange: () => handleSelectFilter('productType', 'Ready to Eat'),
                    isChecked: isChecked('productType', 'Ready to Eat'),
                  },
                  {
                    label: 'Grocery',
                    onChange: () => handleSelectFilter('productType', 'Grocery'),
                    isChecked: isChecked('productType', 'Grocery'),
                  },
                ]}
              />
            </div>
          )}
        </div>
        <div>
          <div
            role="button"
            onClick={() => setIsCookTimeOpen(!isCookTimeOpen)}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setIsCookTimeOpen(!isCookTimeOpen)}
            className={styles.filter_category_header}
          >
            Cook Time
            {isCookTimeOpen ? <ChevronUpIcon width="9px" /> : <ChevronDownIcon width="9px" />}
          </div>
          <hr style={{ marginTop: '12px' }} />
          {isCookTimeOpen && (
            <div className={styles.checkboxes_container}>
              <CheckboxList
                color="#297B03"
                checkboxes={[
                  {
                    label: '2 min or less',
                    onChange: () => handleSelectFilter('cookTime', '2 min'),
                    isChecked: isChecked('cookTime', '2 min'),
                  },
                  {
                    label: '15 min or less',
                    onChange: () => handleSelectFilter('cookTime', '<15 min'),
                    isChecked: isChecked('cookTime', '<15 min'),
                  },
                  {
                    label: '30 min or less',
                    onChange: () => handleSelectFilter('cookTime', '<30 min'),
                    isChecked: isChecked('cookTime', '<30 min'),
                  },
                  {
                    label: '30+ min',
                    onChange: () => handleSelectFilter('cookTime', '30+ min'),
                    isChecked: isChecked('cookTime', '30+ min'),
                  },
                ]}
              />
            </div>
          )}
        </div>
      </div>
    </Dropdown>
  );
}
